/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, ICoreContext } from '@msdyn365-commerce/core';
import React from 'react';

import { IOrderReturnInitiatedDetailsTableHeaderResources, OrderReturnInitiatedDetailsTableHeader } from './order-return-initiated-details-table/order-return-initiated-details-table-header.component';
import { OrderReturnInitiatedDetailsTableRow } from './order-return-initiated-details-table/order-return-initiated-details-table-row.component';

interface IOrderReturnInitiatedDetailsTableData {
    returnGroup: IReturnGroup;
}

export interface IProductReturnRow {
    productName: string;
    productItemId: string;
    productRequestedQuantity: string;
    productQuantity: string;
    productRequestStatus: string;
}

export interface IReturnGroup {
    rmaNumber: string;
    orderNumber: string;
    rows: IProductReturnRow[];
}

export interface IOrderReturnInitiatedDetailsTableProps extends IComponentProps<IOrderReturnInitiatedDetailsTableData> {
    context: ICoreContext;
    className: string;

    resources: IOrderReturnInitiatedDetailsTableResources;
}

export interface IOrderReturnInitiatedDetailsTableResources extends IOrderReturnInitiatedDetailsTableHeaderResources {
    tableHeaderOrderNumberLabel: string;
    rmaNumberLabel: string;
}

export interface IOrderReturnInitiatedDetailsTableComponent extends IComponent<IOrderReturnInitiatedDetailsTableProps> {}

/**
 *  Table component. Renders a single return (one RMA) as a table with its own order / RMA caption.
 * @param props - Configuration of the component.
 * @returns React node for the table.
 */
export const OrderReturnInitiatedDetailsTableComponent: React.FC<IOrderReturnInitiatedDetailsTableProps> = (props: IOrderReturnInitiatedDetailsTableProps) => {

    const rowClassName = `${props.className}__row`;
    const headerClassName = `${props.className}__header`;
    const { returnGroup } = props.data;

    return (
        <div className={`${props.className}__group`}>
            <div className={`${props.className}__group-caption`}>
                <span className={`${props.className}__group-order-number`}>
                    {`${props.resources.tableHeaderOrderNumberLabel}${returnGroup.orderNumber}`}
                </span>
                {returnGroup.rmaNumber &&
                    <span className={`${props.className}__group-rma-number`}>
                        {`${props.resources.rmaNumberLabel}${returnGroup.rmaNumber}`}
                    </span>
                }
            </div>
            <table className={`${props.className}__table`}>
                <OrderReturnInitiatedDetailsTableHeader
                    context={props.context}
                    typeName={props.typeName}
                    id={headerClassName}
                    className={headerClassName}
                    resources={props.resources}
                    data={returnGroup.rows}
                />
                <tbody>
                    {returnGroup.rows.map((row, index) => {
                        return (
                            <OrderReturnInitiatedDetailsTableRow
                                context={props.context}
                                typeName={props.typeName}
                                id={`${rowClassName}-${index}`}
                                key={`${row.productItemId}-${index}`}
                                className={rowClassName}
                                data={{ rowData: row }}
                            />
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
